
import React, { useState, useEffect } from 'react';
import { ShieldAlert, RefreshCw } from 'lucide-react';
import { SiteSettings } from '../types';

interface AdBlockDetectorProps {
  settings: SiteSettings;
}

const AdBlockDetector: React.FC<AdBlockDetectorProps> = ({ settings }) => {
  const [isAdBlockDetected, setIsAdBlockDetected] = useState(false);
  const [loading, setLoading] = useState(true);

  const enabled = settings.enableAdBlockDetector || false;
  const title = settings.adBlockTitle || 'Bloqueador Detectado';
  const message = settings.adBlockMessage || 'Para continuar navegando em nosso portfólio e acessando os conteúdos exclusivos, por favor, desative seu bloqueador de anúncios (AdBlock).';
  const buttonText = settings.adBlockButtonText || 'Já Desativei, Atualizar';

  useEffect(() => {
    // Se a função estiver desativada nas configurações, não fazemos nada
    if (!enabled) {
      setLoading(false);
      return;
    }

    const adBlockCheck = async () => {
      try {
        await fetch('https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js', {
          method: 'HEAD',
          mode: 'no-cors',
        });
        setIsAdBlockDetected(false);
      } catch (error) {
        console.warn('AdBlock Detectado:', error);
        setIsAdBlockDetected(true);
      } finally {
        setLoading(false);
      }
    };

    adBlockCheck();
  }, [enabled]);

  const handleRefresh = () => {
    window.location.reload();
  };

  if (!enabled || loading || !isAdBlockDetected) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border-2 border-red-500 relative">
        <div className="bg-red-500 p-6 flex justify-center">
            <div className="bg-white/20 p-4 rounded-full backdrop-blur-sm animate-pulse">
                <ShieldAlert size={64} className="text-white" />
            </div>
        </div>
        
        <div className="p-8 text-center">
          <h2 className="text-2xl font-black text-gray-900 mb-4 uppercase tracking-tight leading-tight">
            {title}
          </h2>
          
          <p className="text-gray-600 font-medium leading-relaxed mb-8">
            {message}
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-8 text-left border border-gray-100">
             <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Como desativar:</p>
             <ul className="text-[11px] text-gray-700 space-y-2 font-medium">
                <li className="flex items-center gap-2">1. Clique no ícone do AdBlock no navegador.</li>
                <li className="flex items-center gap-2">2. Selecione "Pausar neste site" ou "Não executar em páginas deste domínio".</li>
                <li className="flex items-center gap-2">3. Clique no botão abaixo para atualizar.</li>
             </ul>
          </div>

          <button 
            onClick={handleRefresh}
            className="w-full py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-black uppercase tracking-widest shadow-xl shadow-red-500/30 transition-all transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3"
          >
            <RefreshCw size={20} /> {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdBlockDetector;
